import type { CSSObject } from '@emotion/react'
import classNames from 'classnames'
import {
  useSelect,
  UseSelectState,
  UseSelectStateChangeOptions,
  UseSelectStateChange,
} from 'downshift'
import uniqueId from 'lodash/uniqueId'
import React, {
  useMemo,
  useState,
  useRef,
  RefObject,
  useEffect,
  useCallback,
} from 'react'
import { Color, Theme } from '@opallabs/gem'
import { useDropdownPositioning, useTheme } from '@opallabs/hooks'
import { useOnce } from '@opallabs/lambda'
import { ColorLabelToken } from '../../ColorToken'
import { Icon } from '../../Icon'
import { DeprecatedMenuOption, OptionIcon } from '../../MenuOption/deprecated'
import { Portal } from '../../Portal'
import { TextInput, TextInputType } from '../../TextInput'
import { Tooltip, TooltipProps } from '../../Tooltip'
import { useRadixDropdownBlurHelper } from '../../utils/RadixDropdownBlurHelper'
import * as styles from './styles'
import { DeprecatedSingleSelectOption } from './types'

const PERSIST_PLACEHOLDER_ID = 'persist-placeholder'

type UnwrappedValue<T> =
  T extends DeprecatedSingleSelectOption<infer V> ? V : never

/**
 * @note Use the onChange callback if you want to manage your own
 * selection state or make upstream changes after every change.
 *
 * The initialSelection prop should represent state after any data has been
 * recieved from the server. Downshift manages its own state, and won't update
 * the intial selection after the component has mounted via
 * state management/useEffect.
 */
interface SingleSelectProps<Option> {
  ariaLabel?: string
  /** Use a single child to override the default display for selected items */
  children?: React.ReactElement<{ selectedItem: Option | null }>
  className?: string
  clearInvalidSelection?: boolean
  colorsEnabled?: boolean
  disabled?: boolean
  externalRef?: RefObject<HTMLButtonElement>
  hasError?: boolean
  htmlId?: string
  initialIsOpen?: boolean
  initialSelection?: Option | null
  inputWidth?: number | string
  isClearable?: boolean
  isSearchable?: boolean
  label?: string
  menuWidth?: string | number
  onBlur?: () => void
  onChange?: (selectedItem: Option | null) => void
  onOpenChange?: (isOpen: boolean) => void
  onStaticOptionSelect?: () => void
  options: Option[]
  persistentOption?: React.ReactNode
  hidePersistentOptionOnSearch?: boolean
  placeholder?: string
  selectedItem?: Option
  styleOverrides?: CSSObject
  tooltipProps?: TooltipProps
  tabIndex?: number
  updateInitialSelectionOnPropChange?: boolean
  theme?: Theme
}

/**
 *
 * @deprecated Prefer composable SingleSelect for new components
 */
export const DeprecatedSingleSelect = <
  Option extends DeprecatedSingleSelectOption<OptionValue>,
  OptionValue extends string | number | boolean = UnwrappedValue<Option>,
>({
  ariaLabel = 'Open select menu...',
  children,
  className,
  clearInvalidSelection = false,
  colorsEnabled = false,
  disabled = false,
  externalRef,
  hasError = false,
  htmlId,
  initialIsOpen = false,
  initialSelection,
  inputWidth = '100%',
  isClearable = false,
  isSearchable = false,
  label,
  menuWidth,
  onBlur,
  onChange,
  onOpenChange,
  onStaticOptionSelect,
  options,
  persistentOption,
  hidePersistentOptionOnSearch = false,
  placeholder = 'Select...',
  selectedItem: propSelectedItem,
  styleOverrides,
  tooltipProps,
  tabIndex,
  theme: propTheme,
  updateInitialSelectionOnPropChange = false,
}: SingleSelectProps<Option>) => {
  const providedTheme = useTheme()
  const theme = propTheme ?? providedTheme

  const [searchTerm, setSearchTerm] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const [componentId] = useState(uniqueId('lapidary-single-select-'))
  const selectedItemRef = useRef<Option | null>(initialSelection ?? null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [menuContainerRef, setMenuContainerRef] = useState<
    RefObject<HTMLDivElement>
  >({ current: null })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const toggleRef = externalRef ?? buttonRef

  const stateReducer = (
    state: UseSelectState<Option>,
    actionAndChanges: UseSelectStateChangeOptions<Option>
  ): Partial<UseSelectState<Option>> => {
    const { changes, type } = actionAndChanges
    switch (type) {
      case useSelect.stateChangeTypes.ToggleButtonKeyDownArrowUp:
        return {
          ...changes,
          highlightedIndex:
            // Force state change if pressing Up on first item, so that
            // the `onStateChange` handler can focus the search input
            isSearchable &&
            state.highlightedIndex === 0 &&
            changes.highlightedIndex === 0
              ? -1
              : changes.highlightedIndex,
        }
      case useSelect.stateChangeTypes.ToggleButtonBlur:
        return {
          ...changes,
          // keep the menu open if focusing search
          isOpen: true,
          highlightedIndex:
            isSearchable && searchFocused ? -1 : changes.highlightedIndex,
        }
      case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
      case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
      case useSelect.stateChangeTypes.ItemClick:
        if (changes.selectedItem?.value === PERSIST_PLACEHOLDER_ID) {
          onStaticOptionSelect?.()
          return { ...changes, selectedItem: null }
        }
        if (
          isClearable &&
          changes.selectedItem?.value === state.selectedItem?.value
        ) {
          return {
            ...changes,
            selectedItem: null,
          }
        }
        break
    }
    return changes
  }

  const onStateChange = ({
    type,
    highlightedIndex,
  }: UseSelectStateChange<Option>) => {
    switch (type) {
      case useSelect.stateChangeTypes.ToggleButtonKeyDownArrowUp:
        // If pressing Up on first menu option and `isSearchable = true`,
        // focus the search input
        if (highlightedIndex === -1 && isSearchable) {
          focusSearch()
        }
    }
  }

  const onIsOpenChange = ({ isOpen = false }: UseSelectStateChange<Option>) => {
    onOpenChange?.(isOpen)

    if (isSearchable) {
      if (isOpen) {
        focusSearch()
      } else if (searchTerm !== '') {
        setSearchTerm('')
      }
    }
  }

  // called on item click and enter keypress (from state reducer)
  const onSelectedItemChange = ({
    selectedItem: newSelectedItem,
  }: UseSelectStateChange<Option>) => {
    onChange?.(newSelectedItem ?? null)
    selectedItemRef.current = newSelectedItem ?? null
  }

  const filteredOptions = useMemo(() => {
    const persistentOptionPlaceholder = {
      value: PERSIST_PLACEHOLDER_ID,
      label: 'Persistent Option',
    } as Option

    if (!persistentOption && !searchTerm) {
      return options
    }
    if (!searchTerm && persistentOption) {
      return [...options, persistentOptionPlaceholder]
    }

    const searchFilteredOptions = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
    if (!persistentOption) {
      return searchFilteredOptions
    }
    // Hide persistent option when searching and hidePersistentOptionOnSearch is true
    if (searchTerm && hidePersistentOptionOnSearch) {
      return searchFilteredOptions
    }
    return [...searchFilteredOptions, persistentOptionPlaceholder]
  }, [options, persistentOption, searchTerm, hidePersistentOptionOnSearch])

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    setHighlightedIndex,
    selectedItem,
    getItemProps,
    closeMenu,
    selectItem,
  } = useSelect<Option>({
    stateReducer,
    selectedItem: propSelectedItem,
    onSelectedItemChange,
    onIsOpenChange,
    onStateChange,
    initialIsOpen,
    initialSelectedItem: initialSelection,
    isItemDisabled: (item) => !!item.disabled,
    items: filteredOptions,
    itemToString: (item) => (item ? item.label : ''),
    id: componentId,
  })

  const isSelected = (option: Option) =>
    selectedItem === option || selectedItem?.value === option.value

  const isInvalidSelection = !options.find(
    (o) => o.value === selectedItem?.value
  )

  const focusSearch = () => {
    if (isSearchable) {
      setSearchFocused(true)
      setTimeout(() => searchRef.current?.focus(), 0)
    }
  }

  const onSearchBlur = (e: React.FocusEvent) => {
    setSearchFocused(false)
    // If search loses focus, and search is still highlighted (index -1),
    // then focus was not given to the menu. Ergo, some outside element
    // gained focus, and we should close the menu
    // Ignore if relatedTarget is the toggle button, this happens when
    // you use ArrowDown to navigate to the menu items
    if (
      highlightedIndex === -1 &&
      e.relatedTarget !== toggleRef.current &&
      !searchRef.current
    ) {
      onBlur?.()
      closeMenu()
    }
  }

  const onSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setTimeout(() => closeMenu(), 0)
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (filteredOptions.length === 0) return
      toggleRef.current?.focus()
      setHighlightedIndex(0)
    }
  }

  const onMenuBlur = (e: React.FocusEvent) => {
    if (e.relatedTarget !== searchRef.current) {
      onBlur?.()
    }
  }

  const handleMenuContainerRefChange = useCallback(
    (node: HTMLDivElement | null) => setMenuContainerRef({ current: node }),
    [setMenuContainerRef]
  )

  // Workaround to fix outside click interactions with OptionCell dropdown
  useRadixDropdownBlurHelper(isOpen, [menuContainerRef, toggleRef], () => {
    onBlur?.()
    closeMenu()
  })

  // Apply smart positioning to dropdown menu
  const { isPositioned } = useDropdownPositioning(
    toggleRef.current,
    menuContainerRef.current
  )

  useEffect(() => {
    if (!updateInitialSelectionOnPropChange) return
    if (initialSelection?.value === selectedItemRef.current?.value) return

    const newValue = initialSelection ? initialSelection : null
    selectItem(newValue)
    selectedItemRef.current = newValue
  }, [updateInitialSelectionOnPropChange, initialSelection, selectItem])

  useEffect(() => {
    if (clearInvalidSelection && isInvalidSelection) {
      selectItem(null)
    }
  }, [clearInvalidSelection, isInvalidSelection, selectItem])

  useOnce(() => {
    if (initialIsOpen && isSearchable) focusSearch()
  })

  return (
    <div
      css={[styles.inputWrapper, styleOverrides]}
      className={classNames('lapidary-single-select', className)}
    >
      {label && (
        <label css={styles.inputLabel(theme)} {...getLabelProps()}>
          {label}
        </label>
      )}
      <Tooltip
        {...tooltipProps}
        content={tooltipProps?.content ?? ''}
        disabled={!tooltipProps?.content}
      >
        <button
          type="button"
          className="single-select-value-input"
          css={styles.inputContainer({ theme, isOpen, hasError })}
          style={{ width: inputWidth }}
          {...getToggleButtonProps({
            disabled,
            tabIndex,
            id: htmlId,
            ref: toggleRef,
            onBlur: onMenuBlur,
            // Manually set aria-activedescendant to search input when focused
            // Don't set otherwise, so Downshift can handle the list items
            ...(searchFocused
              ? { 'aria-activedescendant': `${componentId}-search` }
              : {}),
            ...(label === undefined && {
              'aria-label': ariaLabel,
            }),
          })}
        >
          {children && React.Children.only(children) ? (
            React.cloneElement(children, { selectedItem })
          ) : (
            <div css={styles.selectionContent}>
              {selectedItem && (
                <OptionIcon
                  icon={selectedItem.leadingIcon}
                  disabled={disabled}
                  ariaHidden
                />
              )}
              {selectedItem?.color && colorsEnabled ? (
                <ColorLabelToken
                  color={selectedItem?.color}
                  label={selectedItem?.label ?? placeholder}
                />
              ) : (
                <span
                  aria-invalid={isInvalidSelection}
                  css={styles.selectionText({
                    theme,
                    isPlaceholder: !!placeholder && !selectedItem?.label,
                  })}
                >
                  {selectedItem?.label ?? placeholder}
                </span>
              )}
              {selectedItem && (
                <OptionIcon
                  icon={selectedItem.trailingIcon}
                  disabled={disabled}
                  ariaHidden
                />
              )}
            </div>
          )}
          <Icon
            glyph="gem-chevron-s"
            className="single-select-toggle"
            ariaHidden
            color={Color[theme].Text.Default}
          />
        </button>
      </Tooltip>

      <Portal>
        <div
          style={{
            width: menuWidth,
            display: !isOpen ? 'none' : undefined,
            visibility: !isPositioned ? 'hidden' : undefined,
          }}
          id={`${componentId}-menu-container`}
          ref={handleMenuContainerRefChange}
          css={styles.menu({
            theme,
            optionCount: filteredOptions.length,
          })}
        >
          {isOpen && isSearchable && (
            <div css={styles.searchContainer({ theme })}>
              <TextInput
                ref={searchRef}
                htmlId={`${componentId}-search`}
                controlsHtmlId={`${componentId}-menu`}
                type={TextInputType.Search}
                additionalAriaLabels={{ 'aria-label': 'Search options list' }}
                clearable={true}
                leadingIconGlyph="gem-search"
                onChange={(val) => setSearchTerm(val)}
                onInputBlur={onSearchBlur}
                onKeyDown={onSearchKeyDown}
                placeholder=""
                theme={theme}
              />
            </div>
          )}
          <ul css={styles.menuContent} {...getMenuProps()}>
            {isOpen &&
              filteredOptions.map((option, index) => {
                if (option.value !== PERSIST_PLACEHOLDER_ID) {
                  return (
                    <DeprecatedMenuOption
                      colorsEnabled={colorsEnabled}
                      color={option.color ?? undefined}
                      disabled={option.disabled}
                      highlightedByKeyboard={highlightedIndex === index}
                      key={String(option.value)}
                      leadingIcon={option.leadingIcon}
                      trailingIcon={option.trailingIcon}
                      optionText={option.label}
                      selected={isSelected(option)}
                      theme={theme}
                      {...getItemProps({
                        index,
                        item: option,
                      })}
                    />
                  )
                }
                return (
                  <li
                    key={option.value}
                    css={styles.persistentOption(
                      theme,
                      highlightedIndex === index
                    )}
                    {...getItemProps({
                      item: option,
                      index,
                    })}
                  >
                    {persistentOption}
                  </li>
                )
              })}
            {isOpen &&
              !filteredOptions.length &&
              (!persistentOption ||
                (searchTerm && hidePersistentOptionOnSearch)) && (
                <li key="no-options" css={styles.noOptions}>
                  No options available
                </li>
              )}
          </ul>
        </div>
      </Portal>
    </div>
  )
}
